import Cookies from '../../providers/Cookies';
import ucmCountryCodes from './ucmCountryCodeMap';

const { version } = require('../../../../../package.json');

// responsible for:
// presenting UCM dialogue to the end user
// configuring gate-keeping scripts that write cookies based on the user's consent

// Latest Script Supplier versions and SRI hashes can be found here: https://github.aexp.com/amex-eng/axp-script-supplier#install-script-supplier
const scriptSupplierEnvs = {
  // localhost
  dev: {
    url: 'https://cdaas-dev.americanexpress.com/one/axp-script-supplier/6.0.5/script-supplier.js',
    sriHash: 'sha256-ExiciGBlfGDqE+R3hcMQCe4F6RR1FviaKg7rsxfK+CM=',
  },
  // dev environment
  devExternal: {
    url: 'https://cdaas-dev.americanexpress.com/one/axp-script-supplier/6.0.5/script-supplier.js',
    sriHash: 'sha256-ExiciGBlfGDqE+R3hcMQCe4F6RR1FviaKg7rsxfK+CM=',
  },
  staging: {
    url: 'https://cdaas-dev.americanexpress.com/one/axp-script-supplier/6.0.5/script-supplier.js',
    sriHash: 'sha256-ExiciGBlfGDqE+R3hcMQCe4F6RR1FviaKg7rsxfK+CM=',
  },
  demo: {
    url: 'https://qwww.aexp-static.com/cdaas/one/axp-script-supplier/6.0.5/script-supplier.js',
    sriHash: 'sha256-pKlzoZvO5B05MzWuUoSf4t9UYLvhQdUdaoqNeQgc6KA=',
  },
  sandbox: {
    url: 'https://qwww.aexp-static.com/cdaas/one/axp-script-supplier/6.0.5/script-supplier.js',
    sriHash: 'sha256-pKlzoZvO5B05MzWuUoSf4t9UYLvhQdUdaoqNeQgc6KA=',
  },
  prod: {
    url: 'https://www.aexp-static.com/cdaas/one/axp-script-supplier/6.0.5/script-supplier.js',
    sriHash: 'sha256-q0IeF4SZx1Plg+tjFlB2Jf7Ij4LKfYYg7ESpLTkW35c=',
  },
};

// we reload the page when consent changes
function consentChangeHandler() {
  window.location.reload(true);
}

/**
 * countryCode - whether the UCM dialogue is presented is based on whether or not the user is located in a consent market while using the application
 * This value drives that eg en-GB is Great Britain and is a consent market
 * eg 'US' Also see the ucmCountryCodeMap
 */
function loadScriptSupplier(countryCode, allowedCountries = []) {
  // initialize the ResyCookies and ResyUCM API, which will be populated by script supplier according to consent.
  window.ResyCookies = Cookies;
  window.ResyUCM = {
    setEssentialCookie: () => { },
    setFunctionalCookie: () => { },
    setMarketingCookie: () => { },
    setPerformanceCookie: () => { },
  };

  // this is to satisfy Adobe
  window.countryCode = countryCode;

  /**
   * This is for being able to locally test User Consent Management.
   * In addition to the url param 'test_ucm_locale' set to a consent market value such as 'en-GB'
   * you will need to disable CORS when developing locally by either:
   * open -na "Google Chrome" --args --disable-web-security --user-data-dir=$HOME/development_profile
   * or using an extension that disables CORs
   * https://chromewebstore.google.com/detail/allow-cors-access-control/lhobafahddgcelffkeicbaginigeejlf
   */
  const urlParams = new URLSearchParams(window.location.search);
  const testUCMLocale = urlParams.get('test_ucm_locale');

  // AMEX UCM team doesn't support every market that Resy does
  // Therefore, the ucmCountryCodeMap has every country they support + non-consent markets
  // If the given country (Ex: Portugal) requires consent, but is not on the map, we default to en-GB (consent market)
  window.scriptSupplierPageLocale = testUCMLocale || (countryCode && ucmCountryCodes[countryCode]) || 'en-GB';

  // UCMLanguageOverride is used to specify what language the banner should appear in
  // We send it as resy_<language> to match the expected format in the UCM repo here: https://github.aexp.com/amex-eng/user-consent-management/blob/main/src/granularBanner.js#L65-L75
  // The UCM resy lang packs can be found here: https://github.aexp.com/amex-eng/user-consent-management/tree/main/optInLocales/resy
  window.UCMLanguageOverride = `resy_${window.preferredLanguage}`;

  // allowedCountries is derived from the ads_web_geo_enabled_country experiment flag and passed in by the caller
  const mappedLocale = ucmCountryCodes[countryCode] || '';
  const isGptMarketingEnabled = Array.isArray(allowedCountries) && allowedCountries.some(
    (c) => c.toLowerCase() === mappedLocale.toLowerCase()
  );

  // The names and versions below correspond to scripts in shared-scripts-version-map that are loaded through script supplier
  // Version map can be found here: https://github.aexp.com/amex-eng/shared-scripts-version-map
  window.scriptSupplierPreset = [
    {
      name: 'resy-only-double-click',
      version: '^1.0.1',
    },
    // Note: will be moving to the generic version and deprecating resy-only-double-click
    // {
    //   name: 'resy-only-double-click-generic',
    //   version: '^1.0.0',
    // },
    {
      name: 'resy-only-fb-pixel',
      version: '^1.0.2',
      async: true,
    },
    {
      name: 'resy-only-reddit-pixel',
      version: '1.4.0',
      async: true,
    },
    {
      name: 'resy-only-floodlight',
      version: '^1.0.4',
      async: true,
    },
    // Note: removed below while we wait for dxt changes to make it to prod
    // {
    //   name: 'resy-only-google-pixel-generic',
    //   version: '^1.0.0',
    // },
    {
      name: 'resy-only-simon-generic',
      version: '^1.1.0',
      config: {
        debug: !((ENV === 'dev' || ENV === 'prod')),
      },
      async: true,
    },
    {
      name: 'resy-only-trade-desk',
      version: '^1.0.3',
    },
    {
      name: 'resy-only-ucm-essential',
      version: '^1.0.0',
    },
    {
      name: 'resy-only-ucm-functional',
      version: '^1.0.0',
    },
    {
      name: 'resy-only-ucm-marketing',
      version: '^1.0.0',
    },
    {
      name: 'resy-only-ucm-performance',
      version: '^1.0.0',
    },
    {
      name: 'unsafe-datadog-rum',
      version: '^1.0.2',
      config: {
        applicationId: '2bbbad6e-67cb-4049-a31e-8384e15fbd90',
        clientToken: 'pub6398a4c636ff6803345df9156eedeefa',
        site: 'datadoghq.com',
        service: 'resy-web',
        env: ENV === 'prod' ? 'production' : ENV,
        version,
        /**
         * Sample rates must be LOW!!
         * We are billed based on this, so careful consideration pls
         */
        sessionSampleRate: 100,
        sessionReplaySampleRate: 5, // % of sessions to record replays for
        // RUM → APM linking (v4)
        traceSampleRate: 50, // % of eligible requests to inject trace headers
        allowedTracingOrigins: [
          /^https:\/\/(?!blog\.|tr\.snapchat\.com)[^/]*\.resy\.com($|\/.*)/,
          /^ https: \/\/.*\.resyos\.com/,
        ],
        excludedActivityUrls: [
          /^https:\/\/tr\.snapchat\.com($|\/.*)/,
        ],
        traceContextInjection: 'sampled', // allows backend tracing libraries to make their own sampling decisions for sessions where the RUM SDK decides not to keep the trace
        trackInteractions: true,
        trackAnonymousUser: true,
        trackResources: true,
        trackLongTasks: true,
        defaultPrivacyLevel: 'mask-user-input', // masks user inputs while preserving non-input text
        // frustration signals
        // https://docs.datadoghq.com/real_user_monitoring/frustration_signals/
        trackUserInteractions: true,
        trackFrustrations: true,
      },
    },
    {
      name: 'adobe',
      version: '^1.0.0',
      async: true,
      config: {
        market: window.scriptSupplierPageLocale,
        businessUnit: 'travel',
      },
    },
    {
      name: 'resy-os-web-only-amplitude-with-guides',
      version: '^1.0.0',
    },
    ...(isGptMarketingEnabled ? [{
      name: 'gpt-marketing',
      version: '^1.0.4',
    }] : []),
  ];

  // Post consent handlers can be passed to window.EuCookieConsentHandlers
  // In our case we reload the page so the consent is saved and takes effect
  if (!Array.isArray(window.EuCookieConsentHandlers)) {
    window.EuCookieConsentHandlers = [];
  }
  window.EuCookieConsentHandlers.push(consentChangeHandler);

  const script = document.createElement('script');
  script.async = true;
  script.defer = true;
  script.crossOrigin = 'anonymous';
  script.src = scriptSupplierEnvs[ENV] && scriptSupplierEnvs[ENV].url;
  script.integrity = scriptSupplierEnvs[ENV] && scriptSupplierEnvs[ENV].sriHash;
  script.id = 'script-supplier';
  document.body.appendChild(script);
}

export default loadScriptSupplier;
